import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MeasureActions } from '../measure.actions';
import { Store } from '@ngrx/store';
import { selectCurrentSurveyVersionId } from '../measure.selector';
import { LikertService } from '../../services/likert.service';
import { defaultQuestionPlaceholdersConfig } from '../../config/default-question-placeholders.config';

@Injectable()
export class LikertEffects {
  currentMeasureVersionId = this.store.select(selectCurrentSurveyVersionId);

  addLikert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addLikertScale),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.likertService.addLikertScale(action.questionId, defaultQuestionPlaceholdersConfig.likertColumnName).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.addChoiceFailure())),
        ),
      ),
    ),
  );

  updateLikert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateLikertDisplay),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.likertService.updateLikertScaleDisplay(action.likertId, action.display).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.addChoiceFailure())),
        ),
      ),
    ),
  );

  updateLikertValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateLikertValue),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.likertService.updateLikertScaleValue(action.likertId, action.value).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.updateLikertValueFailure())),
        ),
      ),
    ),
  );

  removeLikert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeLikertScale),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.likertService.removeLikertScale(action.likertId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError(() => of(MeasureActions.removeChoiceFailure())),
        ),
      ),
    ),
  );

  updateLikertCustomValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateLikertCustomValue),
      withLatestFrom(this.currentMeasureVersionId),
      filter(([_, surveyVersionId]) => surveyVersionId !== null && surveyVersionId !== undefined),
      switchMap(([action, surveyVersionId]) =>
        this.likertService.updateLikertScaleCustomValue(action.likertId, action.customValue).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((err) =>
            of(
              MeasureActions.updateLikertCustomValueFailure({
                error: err?.message ?? 'updateLikertCustomValue failed',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private likertService: LikertService,
  ) {}
}
